import {z as zod} from "zod";
import type {Integer, Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {PaymentCurrency} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PaymentCurrency";
import {
    integerZodValidator,
    iso8601DateTimeZodValidator,
    uuidZodValidator,
} from "@vesta/global-common-typescript/validators/zodValidators";

export enum InvoiceStatus {
    abandoned = "cc4dd1b3-dca0-4b57-b46f-a6273586e664",
    automatedPaymentFailed = "a666a00a-606d-4f3c-8c36-abefd853e6bb",
    manualPaymentFailed = "3e4ec2ed-8bc4-4393-8551-c8a27bf6b1e9",
    paid = "cd210b73-67b6-44c2-b69c-87a9c573bd34",
    preDebitNotificationSent = "ef93fd89-c523-4deb-bb69-20b79568e9b7",
    processing = "64433fe1-0ac8-44d3-a917-5115c6e98123",
    unpaid = "f0bb5941-2e12-4f92-ae39-3f0cf3eb3d48",
}

export enum InvoiceType {
    normal = "a130b6ad-f476-4cc9-85fc-50e40812c679",
    zeroAmount = "6fa21e25-790d-4241-b644-3bc52c890d30",
}

export type InvoiceLineItem = {
    amount: Integer;
    title: string;
};

export type Invoice = {
    amount: Integer;
    createdAt: Iso8601DateTime;
    id: Uuid;
    invoiceFileJanusId: Uuid | null;
    invoiceId: string;
    lineItems: Array<InvoiceLineItem>;
    preDebitNotificationId: Uuid | null;
    status: InvoiceStatus;
    subscriptionCycleId: Uuid;
    type: InvoiceType;
    updatedAt: Iso8601DateTime;
    workspaceId: Uuid;
};

export type InvoiceWithSubscriptionPlanDetails = Invoice & {
    paymentCurrency: PaymentCurrency;
    subscriptionPlanDisplayName: string | null;
    subscriptionPlanId: Uuid;
};

export const invoiceLineItemZodValidator = zod.object({
    amount: integerZodValidator,
    title: zod.string(),
});

export const invoiceZodValidator = zod.object({
    amount: integerZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    id: uuidZodValidator,
    invoiceFileJanusId: uuidZodValidator.nullable(),
    invoiceId: zod.string(),
    lineItems: zod.array(invoiceLineItemZodValidator),
    preDebitNotificationId: uuidZodValidator.nullable(),
    status: zod.enum(InvoiceStatus),
    subscriptionCycleId: uuidZodValidator,
    type: zod.enum(InvoiceType),
    updatedAt: iso8601DateTimeZodValidator,
    workspaceId: uuidZodValidator,
});

export const invoiceWithSubscriptionPlanDetailsZodValidator = invoiceZodValidator.extend({
    paymentCurrency: zod.enum(PaymentCurrency),
    subscriptionPlanDisplayName: zod.string().nullable(),
    subscriptionPlanId: uuidZodValidator,
});
